import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Carousel from '@lib/components/Carousel/Carousel';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import { WidgetGrid } from '../../WidgetContent/WidgetContent';
import {
  Location,
  WeatherData,
  HourlyForecast,
  DailyForecast,
} from './types/weather';
import { fetchWeatherData } from './services/weatherService';
import {
  getWeatherDescription,
  getWeatherIconType,
  formatTemperature,
  formatTemperatureRange,
  formatHour,
  formatDayShort,
  getDayOfWeek,
  getCurrentTimeFormatted,
} from './utils/weatherUtils';
import WeatherIcon from './components/WeatherIcon';
import NewBadge from '../../NewBadge';
import styles from './WeatherWidget.module.scss';

export enum WeatherWidgetType {
  Today = 'today',
  Week = 'week',
}

enum WeatherError {
  FetchFailed = 'home.weather-fetch-error',
}

export interface WeatherWidgetProps {
  type: WeatherWidgetType;
  location?: Location;
  grid?: WidgetGrid;
  showBadge?: boolean;
}

// Default location (Lviv, Ukraine for demo)
const DEFAULT_LOCATION: Location = {
  latitude: 49.8397,
  longitude: 24.0297,
  name: 'Lviv, Ukraine',
};

// Calculate slides per view based on grid column width
// Grid has 12 columns total
function getSlidesPerViewForGridWidth(
  gridWidth: number,
  totalItems: number,
): number {
  // Map grid width (1-12 columns) to slides per view
  // Smaller widgets show fewer slides, larger show more
  if (gridWidth <= 3) return Math.min(2, totalItems) - 0.5;
  if (gridWidth <= 4) return Math.min(3, totalItems) - 0.5;
  if (gridWidth <= 5) return Math.min(4, totalItems) - 0.5;
  if (gridWidth <= 6) return Math.min(5, totalItems) - 0.5;
  if (gridWidth <= 8) return Math.min(6, totalItems) - 0.5;
  // Full width or larger - show all items
  return totalItems;
}

function WeatherWidget({
  type,
  location = DEFAULT_LOCATION,
  grid,
  showBadge = false,
}: WeatherWidgetProps) {
  const { t } = useTranslation();
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<WeatherError | null>(null);
  const [currentTime, setCurrentTime] = useState(getCurrentTimeFormatted());

  useEffect(() => {
    const getWeather = async () => {
      try {
        setLoading(true);
        const data = await fetchWeatherData(location);
        setWeatherData(data);
        setError(null);
      } catch {
        setError(WeatherError.FetchFailed);
      } finally {
        setLoading(false);
      }
    };

    getWeather();
  }, [location]);

  // Update time every minute
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(getCurrentTimeFormatted());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  // Get hourly forecast data (next 24 hours from current time)
  const hourlyForecast = useMemo((): HourlyForecast[] => {
    if (!weatherData?.hourly) return [];

    const now = new Date();
    const currentHourIndex = weatherData.hourly.time.findIndex((time) => {
      const hourDate = new Date(time);
      return hourDate >= now;
    });

    if (currentHourIndex === -1) return [];

    const forecast: HourlyForecast[] = [];
    for (
      let i = currentHourIndex;
      i < Math.min(currentHourIndex + 24, weatherData.hourly.time.length);
      i += 1
    ) {
      forecast.push({
        time: weatherData.hourly.time[i],
        temperature: weatherData.hourly.temperature_2m[i],
        weatherCode: weatherData.hourly.weather_code[i],
      });
    }
    return forecast;
  }, [weatherData]);

  // Get daily forecast data (7 days)
  const dailyForecast = useMemo((): DailyForecast[] => {
    if (!weatherData?.daily) return [];

    return weatherData.daily.time.slice(0, 7).map((date, index) => ({
      date,
      dayName: formatDayShort(date),
      tempMax: weatherData.daily.temperature_2m_max[index],
      tempMin: weatherData.daily.temperature_2m_min[index],
      weatherCode: weatherData.daily.weather_code[index],
    }));
  }, [weatherData]);

  // Calculate slidesPerView for week view based on grid width
  const weekSlidesPerView = useMemo(() => {
    const gridWidth = grid?.w ?? 12; // Default to full width if no grid
    return getSlidesPerViewForGridWidth(gridWidth, dailyForecast.length || 7);
  }, [grid?.w, dailyForecast.length]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t('loading')}</div>
      </div>
    );
  }

  if (error || !weatherData) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{t(error ?? 'no-data')}</div>
      </div>
    );
  }

  if (type === WeatherWidgetType.Today) {
    const dayName = getDayOfWeek(
      weatherData.current.time || new Date().toISOString(),
    );
    const description = getWeatherDescription(weatherData.current.weather_code);
    const temp = Math.round(weatherData.current.temperature_2m);

    return (
      <div className={styles.container}>
        {showBadge && <NewBadge className={styles.badge} />}
        <div className={styles.todayWidget}>
          <div className={styles.header}>
            <WeatherIcon
              type={getWeatherIconType(weatherData.current.weather_code)}
              size="large"
            />
            <div className={styles.currentInfo}>
              <Typography variant="caption" className={styles.dateTime}>
                {dayName}, {currentTime}
              </Typography>
              <Typography variant="h3" className={styles.temperature}>
                {description} {temp}°C
              </Typography>
              <div className={styles.location}>
                <MaterialIcon
                  icon="location_on"
                  className={styles.locationIcon}
                  size="md-16"
                  symbolsOutlined
                />
                <Typography variant="caption" className={styles.locationText}>
                  {location.name}
                </Typography>
              </div>
            </div>
          </div>
          <div className={styles.slider}>
            <Carousel
              slidesPerView="auto"
              spaceBetween={0}
              slideWidth={88}
              navigationButtonType={IconButtonTypes.SecondaryOutlined}
              hideNavigationAtEdges
            >
              {hourlyForecast.map((hour, index) => (
                <div key={hour.time} className={styles.timeItemWrapper}>
                  <div className={styles.weatherItem}>
                    <p className={styles.weatherItemTime}>
                      {formatHour(hour.time)}
                    </p>
                    <WeatherIcon
                      type={getWeatherIconType(hour.weatherCode)}
                      size="small"
                    />
                    <p className={styles.weatherItemTemp}>
                      {formatTemperature(hour.temperature)}
                    </p>
                  </div>
                  {index < hourlyForecast.length - 1 && (
                    <div className={styles.divider} />
                  )}
                </div>
              ))}
            </Carousel>
          </div>
        </div>
      </div>
    );
  }

  // Week view
  return (
    <div className={styles.container}>
      {showBadge && <NewBadge className={styles.badge} />}
      <div className={styles.weekWidget}>
        <Carousel
          slidesPerView={weekSlidesPerView}
          spaceBetween={0}
          slideWidth="auto"
          navigationButtonType={IconButtonTypes.SecondaryOutlined}
          hideNavigationAtEdges
        >
          {dailyForecast.map((day, index) => (
            <div key={day.date} className={styles.weekItemWrapper}>
              <div className={styles.weekItem}>
                <Typography variant="caption" className={styles.weekItemDate}>
                  {day.dayName}
                </Typography>
                <WeatherIcon
                  type={getWeatherIconType(day.weatherCode)}
                  size="small"
                />
                <Typography variant="caption" className={styles.weekItemTemp}>
                  {formatTemperatureRange(day.tempMin, day.tempMax)}
                </Typography>
              </div>
              {index < dailyForecast.length - 1 && (
                <div className={styles.divider} />
              )}
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  );
}

export default React.memo(WeatherWidget);
